/**
 * Slim auth shim — replaces the original full auth service.
 *
 * The CRM no longer owns its own login/logout/2FA flow inside the
 * dashboard; the dashboard authenticates the user, and CrmRoot
 * bootstraps a brain crm-api session token that lives in localStorage
 * under `xop_crm_token`. This file exposes just the read-side helpers
 * (token getters, auth header, cached user/tenant) so unmodified CRM
 * source files that import `authService` continue to work without
 * dragging the dead login/2FA code.
 */

export interface User {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  active?: boolean;
  email_verified?: boolean;
  is_super_admin?: boolean;
  two_factor_enabled?: boolean;
  roles?: string[];
  [key: string]: unknown;
}

export interface Tenant {
  id: number;
  name: string;
  slug: string;
  plan?: string;
  [key: string]: unknown;
}

const TOKEN_KEY = "xop_crm_token";
const USER_KEY = "xop_crm_user";
const TENANT_KEY = "xop_crm_tenant";

class AuthShim {
  isAuthenticated(): boolean {
    return Boolean(this.getToken());
  }

  getToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(TOKEN_KEY);
  }

  getUser(): User | null {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  }

  getTenant(): Tenant | null {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem(TENANT_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as Tenant;
    } catch {
      return null;
    }
  }

  /**
   * Headers map for raw `fetch()` calls that bypass the central axios
   * client. The CRM's two admin pages use this pattern.
   */
  getAuthHeader(): Record<string, string> {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Clears local CRM session state. The dashboard owns the actual
   * logout flow — call window.location reassignment from the caller
   * if you also want to navigate.
   */
  clearAuth(): void {
    if (typeof window === "undefined") return;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TENANT_KEY);
    localStorage.removeItem("xop_crm_permissions");
  }
}

const authService = new AuthShim();
export default authService;
